import React from 'react';
import { cn } from '@/utils/styles';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { ConnectionCard } from '@design-system/Cards/ConnectionCard';
import { InfoSheet } from '@screens/HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { EventTracker } from '@/utils';

interface HealthConnection {
  name: string;
  no_of_connections: number | null;
  connection_type: string;
  connection_type_color: string;
}

interface HealthConnectionsProps {
  connections: HealthConnection[];
  isLoading: boolean;
  title: string;
  description: string;
  isProUser?: boolean;
}

interface ConnectionGroup {
  withConnections: HealthConnection[];
  noConnections: HealthConnection[];
}

interface SkeletonViewProps {
  'data-testid'?: string;
}

export const SkeletonView = ({ 'data-testid': testId }: SkeletonViewProps = {}) => (
  <div className="mt-12" data-testid={testId}>
    <Skeleton className="w-3/4 h-6 mb-4 rounded-lg" data-testid="skeletonView-titleSkeleton" />
    <Skeleton className="w-full h-20 rounded-lg" data-testid="skeletonView-descriptionSkeleton" />
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="skeletonView-cardsGrid">
      {Array.from({ length: 10 }).map((_, index) => (
        <Skeleton
          key={`skeleton-${index}`}
          className="w-full h-32 rounded-lg"
          data-testid={`skeletonView-cardSkeleton-${index}`}
        />
      ))}
    </div>
  </div>
);

const cleanHealthConnectionsType = (input: string): string => {
  return input.replace(/\bAssociated\b/g, '').replace(/\s+/g, '');
};

export const HealthConnectionsContainer: React.FC<HealthConnectionsProps> = ({
  connections,
  title,
  description,
  isLoading,
  isProUser,
}) => {
  const navigate = useNavigate();

  const navigateToConditionsPage = (name: string, risk: string) => {
    EventTracker.trackEvent(
      'health',
      `health_healthConnections_${name}_${cleanHealthConnectionsType(risk)}`,
    );
    navigate(`/health/connections/${encodeURIComponent(name)}`);
  };

  const groupedConnections: ConnectionGroup = {
    withConnections: connections.filter(
      (connection) => connection.no_of_connections !== 0,
    ),
    noConnections: connections.filter(
      (connection) => connection.no_of_connections === 0,
    ),
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" />

        {/* Highlighted Connections */}
        {isLoading ? (
          <SkeletonView />
        ) : (
          <div>
            <div>
              <h1 className="dark typography-h1 text-primary mt-12">
                {title}
              </h1>
              <p className="dark typography-body text-secondary mt-4 max-w-[38rem]">
                {description}
                <InfoSheet
                  title={
                    isProUser
                      ? INFO_SHEET_CONTENT.diseaseRisk.title
                      : INFO_SHEET_CONTENT.healthConnections.title
                  }
                  description={
                    isProUser
                      ? INFO_SHEET_CONTENT.diseaseRisk.description
                      : INFO_SHEET_CONTENT.healthConnections.description
                  }
                />
              </p>
            </div>
            {groupedConnections.withConnections.length > 0 && (
              <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {groupedConnections.withConnections.map((connection) => (
                    <div key={connection.name}>
                      <ConnectionCard
                        name={connection.name}
                        noOfConnections={connection.no_of_connections}
                        connectionType={connection.connection_type}
                        connectionTypeColor={connection.connection_type_color}
                        isProUser={isProUser}
                        onClick={() =>
                          navigateToConditionsPage(
                            connection.name,
                            connection.connection_type,
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Risk Connections */}
        {isLoading ? (
          <></>
        ) : (
          groupedConnections.noConnections.length > 0 && (
            <div className="mt-12 mb-24">
              <h2 className="dark typography-h1 text-primary">
                No Associated Health Risk
              </h2>
              <p className="dark typography-body text-secondary mt-4 max-w-[37rem]">
                Good news. Your results indicate 0 score connections to the
                following health conditions.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                {groupedConnections.noConnections.map((connection) => (
                  <button
                    key={connection.name}
                    onClick={() => {
                      navigateToConditionsPage(connection.name, 'noRisk');
                    }}
                    className={cn(
                      'p-5 rounded-lg min-h-12  shadow-md bg-[#42434D73] hover:shadow-lg transition-shadow text-left w-full',
                    )}
                  >
                    <p
                      className="dark text-primary typography-body truncate whitespace-nowrap"
                      title={connection.name}
                    >
                      {connection.name}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )
        )}
      </StandardContainer>
    </div>
  );
};
